import { Dispatch, RootState } from "core/store/store.types";
import { Plan } from "core/types/product.types";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { productSelectors } from "core/store/modules/product";
import { LoadingOverlay } from "components/atoms/loading-overlay";
import { CheckoutSession } from "core/types/transaction.types";
import {
  transactionAsync,
  transactionSelectors,
} from "core/store/modules/transaction";
import { CircularProgress, Grid, Grow } from "@mui/material";
import { Text } from "components/atoms/text";
import { PlanCard } from "components/molecules/plan-card";
import { PlanButtonGroup } from "components/atoms/plan-button-group";

interface OwnProps {}

interface StateProps {
  planList?: Plan[];
  isProductLoading: boolean;
  isTransactionLoading: boolean;
  checkoutSession?: CheckoutSession;
}

interface DispatchProps {
  createTransaction: (product: string) => void;
  resetCheckoutSession: () => void;
}

export type Props = OwnProps & StateProps & DispatchProps;

const SubscriptionPlan: FunctionComponent<Props> = ({
  planList,
  isProductLoading,
  isTransactionLoading,
  checkoutSession,
  createTransaction,
  resetCheckoutSession,
}) => {
  const { t } = useTranslation();

  // ✔ Tab iniziale = CONSIGLIATI
  const [planTab, setPlanTab] = useState<number>(1);
  const [inFlag, setInFlag] = useState(true);
  const [plansToShow, setPlansToShow] = useState<Plan[]>();

  // ✔ Nuove sezioni tab
  // 1 = Consigliati
  // 2 = Trimestrale (90)
  // 3 = Semestrale (180)
  // 4 = Annuale (365)
  const tabDurations: Record<number, number | "consigliati"> = {
    1: "consigliati",
    2: 90,
    3: 180,
    4: 365,
  };

  // Ordinamento prezzi (lasciato identico)
  useEffect(() => {
    if (planList) {
      const sorted = planList.map((plan) => ({
        ...plan,
        prices: [...plan.prices].sort((a, b) => a.duration - b.duration),
      }));
      setPlansToShow(sorted);
    }
  }, [planList]);

  const startTransaction = (id: string) => {
    createTransaction(id);
  };

  // Redirect al checkout (identico)
  useEffect(() => {
    if (checkoutSession?.url) {
      window.open(checkoutSession.url, "_self");
      resetCheckoutSession();
    }
  }, [checkoutSession]);

  // Animazione Grow (identica)
  useEffect(() => {
    if (!inFlag) {
      setTimeout(() => setInFlag(true), 270);
    }
  }, [inFlag]);

  // -----------------------------------------------------
  // 🎯 FILTRI SEZIONI (nuova logica CONSIGLIATI)
  // -----------------------------------------------------
  const filteredPlans = useMemo(() => {
    if (!plansToShow) return [];

    const selected = tabDurations[planTab];

    // ✔ SEZIONE CONSIGLIATI
    if (selected === "consigliati") {
      return plansToShow
        .map((plan) => {
          const monthly = plan.prices.find((p) => p.duration === 30);
          const semestral = plan.prices.find((p) => p.duration === 180);

          if (monthly && plan.name.toLowerCase().includes("basic")) {
            return { plan, price: monthly };
          }

          if (semestral) {
            return { plan, price: semestral };
          }

          return null;
        })
        .filter(Boolean) as { plan: Plan; price: any }[];
    }

    // ✔ ALTRI TAB (90 / 180 / 365)
    return plansToShow
      .map((plan) => {
        const match = plan.prices.find((p) => p.duration === selected);
        return match ? { plan, price: match } : null;
      })
      .filter(Boolean) as { plan: Plan; price: any }[];
  }, [plansToShow, planTab]);

  return (
    <>
      {isTransactionLoading && <LoadingOverlay open={isTransactionLoading} />}

      <Grid item xs={12} p={"50px 0px 20px"}>
        <Text variant="h3" fontWeight={"700"}>
          {t("profile.sub-plan-title")}
        </Text>
        <Text variant="body2" fontWeight={"500"}>
          {t("profile.sub-plan-subtitle")}
        </Text>
      </Grid>

      {/* SELETTORE TABS */}
      <Grid item container spacing={5} justifyContent={"center"} mt={"5px"}>
        <PlanButtonGroup
          actualPlan={planTab}
          setActualPlan={(tab) => {
            setInFlag(false);
            setPlanTab(tab);
          }}
          onClick={() => setInFlag(false)}
        />
      </Grid>

      {/* LISTA PIANI */}
      <Grid
        item
        container
        spacing={5}
        paddingBottom={"50px"}
        justifyContent={"center"}
        mt={"5px"}
      >
        {isProductLoading && (
          <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <CircularProgress color="primary" size={"50px"} />
          </div>
        )}

        {filteredPlans.length === 0 && !isProductLoading && (
          <Grid item xs={12} style={{ textAlign: "center" }}>
            <Text variant="body2">
              {t("profile.no-plans") || "Nessun piano disponibile"}
            </Text>
          </Grid>
        )}

        {filteredPlans.map(({ plan, price }, idx) => (
          <Grow
            in={inFlag}
            {...(inFlag ? { timeout: (idx + 1) * 350 } : {})}
          >
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <PlanCard
                title={
                  <Text variant={"h4"} fontWeight={"800"}>
                    {plan.name}
                  </Text>
                }
                priceForMonth={price.priceForMonth}
                price={price.price}
                description={plan.description ?? ""}
                list={plan.features.map((f) => f.name)}
                currency={price.currency}
                daysDurate={price.duration}
                onButtonPress={() => startTransaction(price.id)}
                isSub
              />
            </Grid>
          </Grow>
        ))}
      </Grid>
    </>
  );
};

export default connect<StateProps, DispatchProps, OwnProps, RootState>(
  (state: RootState) => ({
    planList: productSelectors.getPlanList(state),
    isProductLoading: productSelectors.isProductLoading(state),
    isTransactionLoading: transactionSelectors.isTransactionLoading(state),
    exchangeRate: productSelectors.getExchangeRate(state),
    checkoutSession: transactionSelectors.getCheckoutSession(state),
  }),
  (dispatch: Dispatch) => ({
    createTransaction: (product: string) =>
      dispatch(transactionAsync.createPlanTransaction(product)),
    resetCheckoutSession: () =>
      dispatch(transactionAsync.resetCheckoutSession()),
  })
)(SubscriptionPlan);
